import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Outlet, useMatches } from '@tanstack/react-router';
import { KonnectOnboardingReminderModal } from 'components/modals/KonnectOnboardingReminderModal';
import { HEADER, NAVBAR } from 'config';
import { useCurrentRole } from 'hooks/slices';
import { useIsAdminImpersonating } from 'hooks/slices/useCurrentUser';
import useResponsive from 'hooks/useResponsive';
import { type ReactNode, useState } from 'react';
import DashboardHeader from './header';
import Navbar from './navbar/Navbar';

export const base = {
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: 24,
};

export const lg = {
  paddingTop: 24,
  paddingBottom: 24,
};

const WrapperStyle = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up(NAVBAR.MOBILE_BREAKPOINT)]: {
    display: 'flex',
    minHeight: '100%',
  },
}));

const MainStyle = styled('main')(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  paddingTop: base.paddingTop,
  paddingBottom: base.paddingBottom,
  [theme.breakpoints.up(NAVBAR.MOBILE_BREAKPOINT)]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: lg.paddingTop,
    paddingBottom: lg.paddingBottom,
  },
}));

export default function MainLayout({ children }: { children?: ReactNode }) {
  const isMobile = useResponsive('down', NAVBAR.MOBILE_BREAKPOINT);
  const [open, setOpen] = useState(false);

  const currentRole = useCurrentRole();
  const isPharmacy = currentRole?.resource_type === 'Pharmacy';

  const isAdminImpersonating = useIsAdminImpersonating();

  const matches = useMatches();
  const isFullScreen = matches.some((match) => match.staticData?.fullScreen);

  if (isFullScreen) {
    return children ? children : <Outlet />;
  }

  return (
    <WrapperStyle>
      {isMobile && <DashboardHeader onOpenSidebar={() => setOpen(true)} />}

      <Navbar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <MainStyle>{children ? children : <Outlet />}</MainStyle>

      {isPharmacy && !isAdminImpersonating && <KonnectOnboardingReminderModal />}
    </WrapperStyle>
  );
}
